import { AlertTriangle, Loader2, ShieldCheck, Undo2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import WarningDialog from '@/components/common/WarningDialog';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { AssetSelector } from '@/pages/assetTransfer';
import {
  AssetNav,
  CredentialGate,
  FieldRow,
  OPS_CHAIN,
  OpStatus,
  SessionBar,
  StatusBanner,
  WalletBanner,
} from '@/pages/walletOps/shared';
import {
  ConnectedNetwork,
  getConnectedNetwork,
  getTestWalletLockStatus,
  revertWalletTokens,
  TestWalletLockStatus,
  whitelistWallet,
} from '@/services/testing';
import { AssetTransferCreds, getTokenBalance } from '@/services/tokenTransfer';
import { isValidAddressForChain } from '@/utils/chains';

// ─── Main Page ────────────────────────────────────────────────────────────────

const TestingPage = () => {
  const [credentials, setCredentials] = useState<AssetTransferCreds | null>(
    null,
  );
  const [assetNav, setAssetNav] = useState<AssetNav | null>(null);
  const [testWallet, setTestWallet] = useState('');
  const [network, setNetwork] = useState<ConnectedNetwork | null>(null);
  const [networkError, setNetworkError] = useState(false);

  useEffect(() => {
    getConnectedNetwork()
      .then(setNetwork)
      .catch((err) => {
        console.error('getConnectedNetwork failed:', err);
        setNetworkError(true);
      });
  }, []);

  const [treasuryBalance, setTreasuryBalance] = useState<string | null>(null);
  const [treasuryLoading, setTreasuryLoading] = useState(false);
  const [walletBalance, setWalletBalance] = useState<string | null>(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [lockStatus, setLockStatus] = useState<TestWalletLockStatus | null>(
    null,
  );

  const walletValid =
    !!testWallet && isValidAddressForChain(OPS_CHAIN, testWallet.trim());
  // Reverting the treasury into itself is a no-op at best; catch the paste mistake up front.
  const walletIsTreasury =
    walletValid &&
    !!credentials &&
    testWallet.trim().toLowerCase() === credentials.walletAddress.toLowerCase();

  const loadBalance = async (
    tokenAddress: string,
    address: string,
    setBalance: (b: string | null) => void,
    setLoading: (l: boolean) => void,
  ) => {
    setLoading(true);
    try {
      setBalance((await getTokenBalance(tokenAddress, address)).balance);
    } catch (err) {
      console.error('getTokenBalance failed:', err);
      setBalance(null);
    } finally {
      setLoading(false);
    }
  };

  const refreshTreasury = (nav = assetNav) => {
    if (nav && credentials)
      loadBalance(
        nav.tokenAddress,
        credentials.walletAddress,
        setTreasuryBalance,
        setTreasuryLoading,
      );
  };

  const refreshWallet = (nav = assetNav) => {
    if (!nav || !walletValid) return;
    loadBalance(
      nav.tokenAddress,
      testWallet.trim(),
      setWalletBalance,
      setWalletLoading,
    );
    getTestWalletLockStatus(nav.tokenAddress, testWallet.trim())
      .then(setLockStatus)
      .catch((err) => {
        console.error('getTestWalletLockStatus failed:', err);
        setLockStatus(null);
      });
  };

  const refreshBoth = () => {
    refreshTreasury();
    refreshWallet();
  };

  return (
    <div className="px-4 py-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold">Testing</h1>
          <NetworkBadge network={network} error={networkError} />
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Run a real investment end to end, then take the tokens back. Whitelist
          the tester&apos;s wallet so delivered tokens are not locked for a
          year, then revert every token to the treasury when the test is done.
        </p>
      </div>

      <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">
              Whitelist before completing the investment.
            </p>
            <ol className="mt-1 list-decimal ml-4 space-y-0.5 text-xs">
              <li>Tester runs the normal flow up to “I have paid my wire”.</li>
              <li>Whitelist the tester&apos;s wallet here.</li>
              <li>
                Complete the investment as usual — tokens arrive unlocked.
              </li>
              <li>When the test is done, revert the wallet here.</li>
            </ol>
            <p className="mt-1 text-xs">
              Tokens delivered before the wallet is whitelisted arrive locked.
              They can still be reverted, but only once the wallet is
              whitelisted.
            </p>
          </div>
        </div>
      </div>

      {!credentials ? (
        <CredentialGate onAuthenticate={setCredentials} />
      ) : (
        <div className="space-y-4">
          <SessionBar
            credentials={credentials}
            onLock={() => {
              setCredentials(null);
              setAssetNav(null);
              setTreasuryBalance(null);
              setWalletBalance(null);
              setLockStatus(null);
            }}
          />

          <AssetSelector
            description="Choose the asset the test investment was made in."
            requireNav={false}
            onNavLoaded={(nav) => {
              setAssetNav(nav);
              setWalletBalance(null);
              setLockStatus(null);
              refreshTreasury(nav);
              refreshWallet(nav);
            }}
          />

          {assetNav && (
            <WalletBanner
              label={`Treasury — ${assetNav.symbol} Token Balance`}
              address={credentials.walletAddress}
              balance={treasuryBalance}
              balanceUnit={assetNav.symbol}
              balanceLoading={treasuryLoading}
              onRefresh={() => refreshTreasury()}
            />
          )}

          <Card className={!assetNav ? 'opacity-50 pointer-events-none' : ''}>
            <CardHeader>
              <CardTitle className="text-base">Test Wallet</CardTitle>
              <CardDescription>
                The tester&apos;s Turnkey wallet — the one tokens are delivered
                to.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <FieldRow id="pt-wallet" label="Wallet Address">
                <Input
                  id="pt-wallet"
                  placeholder="0x..."
                  value={testWallet}
                  onChange={(e) => {
                    setTestWallet(e.target.value);
                    setWalletBalance(null);
                    setLockStatus(null);
                  }}
                  onBlur={() => refreshWallet()}
                />
              </FieldRow>
              {testWallet && !walletValid && (
                <p className="text-sm text-destructive">
                  Not a valid address for this chain.
                </p>
              )}
              {walletIsTreasury && (
                <p className="text-sm text-destructive">
                  This is the treasury wallet — enter the tester&apos;s wallet.
                </p>
              )}
              {assetNav && walletValid && !walletIsTreasury && (
                <WalletBanner
                  label={`Test wallet — ${assetNav.symbol} Token Balance`}
                  address={testWallet.trim()}
                  balance={walletBalance}
                  balanceUnit={assetNav.symbol}
                  balanceLoading={walletLoading}
                  onRefresh={() => refreshWallet()}
                />
              )}
              {walletValid && !walletIsTreasury && lockStatus && (
                <LockStatusLine status={lockStatus} symbol={assetNav?.symbol} />
              )}
            </CardContent>
          </Card>

          <WhitelistCard
            credentials={credentials}
            assetNav={assetNav}
            wallet={testWallet.trim()}
            lockStatus={lockStatus}
            ready={!!assetNav && walletValid && !walletIsTreasury}
            onSuccess={() => refreshWallet()}
          />

          <RevertCard
            credentials={credentials}
            assetNav={assetNav}
            wallet={testWallet.trim()}
            walletBalance={walletBalance}
            lockStatus={lockStatus}
            ready={!!assetNav && walletValid && !walletIsTreasury}
            onSuccess={refreshBoth}
          />
        </div>
      )}
    </div>
  );
};

// ─── Network Badge ────────────────────────────────────────────────────────────

const NetworkBadge = ({
  network,
  error,
}: {
  network: ConnectedNetwork | null;
  error: boolean;
}) => {
  const base = 'rounded-full border px-2.5 py-0.5 text-xs font-medium';
  if (error)
    return (
      <span className={`${base} border-red-200 bg-red-50 text-red-700`}>
        Network unreachable
      </span>
    );
  if (!network)
    return (
      <span className={`${base} text-muted-foreground`}>Checking network…</span>
    );
  return (
    <span
      className={`${base} ${network.isMainnet ? 'border-red-200 bg-red-50 text-red-700' : 'border-blue-200 bg-blue-50 text-blue-700'}`}
      title={`chainId ${network.chainId}`}
    >
      {network.name}
      {network.isMainnet ? ' — real tokens' : ''}
    </span>
  );
};

// ─── Lock Status ──────────────────────────────────────────────────────────────

const LockStatusLine = ({
  status,
  symbol,
}: {
  status: TestWalletLockStatus;
  symbol?: string;
}) => {
  if (!status.hasLocker)
    return (
      <p className="text-xs text-muted-foreground">
        This token has no lock module — tokens are never locked.
      </p>
    );
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
      <span
        className={
          status.whitelisted
            ? 'font-medium text-green-700'
            : 'font-medium text-amber-700'
        }
      >
        {status.whitelisted ? 'Whitelisted' : 'Not whitelisted'}
      </span>
      <span className="text-muted-foreground">
        Locked: {parseFloat(status.locked).toLocaleString()} {symbol}
      </span>
      <span className="text-muted-foreground">
        Unlocked: {parseFloat(status.available).toLocaleString()} {symbol}
      </span>
    </div>
  );
};

// ─── Whitelist ────────────────────────────────────────────────────────────────

const WhitelistCard = ({
  credentials,
  assetNav,
  wallet,
  lockStatus,
  ready,
  onSuccess,
}: {
  credentials: AssetTransferCreds;
  assetNav: AssetNav | null;
  wallet: string;
  lockStatus: TestWalletLockStatus | null;
  ready: boolean;
  onSuccess: () => void;
}) => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<OpStatus>({ type: null, message: '' });

  const handleWhitelist = async () => {
    if (!assetNav) return;
    setLoading(true);
    setStatus({ type: null, message: '' });
    try {
      const result = await whitelistWallet(
        credentials,
        assetNav.tokenAddress,
        wallet,
      );
      setStatus({
        type: 'success',
        message: `${wallet} is whitelisted — ${assetNav.symbol} delivered to it will not be locked.`,
        txHash: result.txHash,
        extra: 'You can now complete the test investment.',
      });
      onSuccess();
    } catch (err: any) {
      setStatus({
        type: 'error',
        message: err?.message ?? 'Whitelist failed.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className={!ready ? 'opacity-50 pointer-events-none' : ''}>
      <CardHeader>
        <CardTitle className="text-base">1. Whitelist Wallet</CardTitle>
        <CardDescription>
          Exempts the test wallet from the one-year token lock, so the tokens
          can be reverted later. Do this before completing the investment.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <StatusBanner status={status} />
        <Button
          className="w-full"
          onClick={handleWhitelist}
          disabled={!ready || loading || !!lockStatus?.whitelisted}
        >
          {lockStatus?.whitelisted ? (
            <>
              <ShieldCheck className="mr-2 h-4 w-4" />
              Already Whitelisted
            </>
          ) : loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Whitelisting...
            </>
          ) : (
            <>
              <ShieldCheck className="mr-2 h-4 w-4" />
              Whitelist Wallet
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

// ─── Revert ───────────────────────────────────────────────────────────────────

const RevertCard = ({
  credentials,
  assetNav,
  wallet,
  walletBalance,
  lockStatus,
  ready,
  onSuccess,
}: {
  credentials: AssetTransferCreds;
  assetNav: AssetNav | null;
  wallet: string;
  walletBalance: string | null;
  lockStatus: TestWalletLockStatus | null;
  ready: boolean;
  onSuccess: () => void;
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<OpStatus>({ type: null, message: '' });

  // Unknown balance (not loaded / RPC failed) is not treated as empty — the revert reads the
  // balance on-chain itself; this only blocks the button when we know there is nothing to move.
  const knownEmpty = walletBalance !== null && parseFloat(walletBalance) === 0;
  // The lock module rejects moving a non-whitelisted wallet's locked tokens, forcedTransfer
  // included — whitelisting first is what lets the revert take everything.
  const blockedByLock =
    !!lockStatus?.hasLocker &&
    !lockStatus.whitelisted &&
    parseFloat(lockStatus.locked) > 0;

  const handleRevert = async () => {
    if (!assetNav) return;
    setLoading(true);
    setStatus({ type: null, message: '' });
    try {
      const result = await revertWalletTokens(
        credentials,
        assetNav.tokenAddress,
        wallet,
      );
      setStatus({
        type: 'success',
        message: `Reverted ${result.tokens ?? 'all'} ${assetNav.symbol} from ${wallet} to the treasury.`,
        txHash: result.txHash,
      });
      onSuccess();
    } catch (err: any) {
      setStatus({ type: 'error', message: err?.message ?? 'Revert failed.' });
    } finally {
      setLoading(false);
      setConfirmOpen(false);
    }
  };

  return (
    <Card className={!ready ? 'opacity-50 pointer-events-none' : ''}>
      <CardHeader>
        <CardTitle className="text-base">2. Revert Tokens</CardTitle>
        <CardDescription>
          Moves every {assetNav?.symbol ?? ''} token in the test wallet back to
          the treasury wallet it came from.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {blockedByLock && (
          <p className="text-sm text-amber-700">
            {parseFloat(lockStatus!.locked).toLocaleString()} {assetNav?.symbol}{' '}
            in this wallet is still locked. Whitelist the wallet first, then
            revert.
          </p>
        )}
        {knownEmpty && (
          <p className="text-sm text-muted-foreground">
            The test wallet holds no {assetNav?.symbol} — nothing to revert.
          </p>
        )}
        <StatusBanner status={status} />
        <Button
          variant="destructive"
          className="w-full"
          onClick={() => setConfirmOpen(true)}
          disabled={!ready || loading || knownEmpty || blockedByLock}
        >
          <Undo2 className="mr-2 h-4 w-4" />
          Revert All to Treasury
        </Button>
      </CardContent>

      <WarningDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Revert tokens to treasury?"
        description={`Every ${assetNav?.symbol ?? ''} token in ${wallet}${walletBalance !== null ? ` (${walletBalance})` : ''} will move back to the treasury wallet ${credentials.walletAddress}. This is an on-chain transaction and cannot be undone.`}
        buttonText="Revert"
        loading={loading}
        onConfirm={handleRevert}
      />
    </Card>
  );
};

export default TestingPage;
